import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PARTY_ROLES } from "@rentorng/core";
import { api } from "@/lib/api";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { telHref, titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { IconPencil, IconReceipt } from "@/components/icons";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LinkButton, PageHeader, Spinner, Table, Tabs } from "@/components/ui";
import { OptionMark } from "@/components/option-picker";

interface Row {
  id: string;
  name: string;
  roles: string[] | null;
  phone: string | null;
  email: string | null;
  balance: number;
  activeLeases: number;
  /**
   * Where this relationship stands, resolved to its name and its mark by the list query itself.
   *
   * Sent beside the id rather than left to be looked up, so a list of fifty draws fifty marks
   * without fifty requests — and left-joined on the server, because an inner join would silently
   * empty the clients list of everybody nobody has filed. Null on every client until somebody
   * says otherwise, and nothing is drawn for a null.
   */
  statusId?: string | null;
  statusName?: string | null;
  statusTone?: "neutral" | "active" | "attention" | "closed" | null;
}

export function ClientsPage() {
  const [params, setParams] = useSearchParams();
  const role = params.get("role") ?? "";
  const q = params.get("q") ?? "";
  const { vocab, can } = useSession();
  const fmt = useFormat();

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["parties", role, list.q, list.page],
    queryFn: () => api.get<{ parties: Row[]; page: PageMeta }>("/parties", { role, q: list.q, page: list.page }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.parties ?? [];
  const tabs = [
    { to: "/app/clients", label: "Everyone" },
    ...PARTY_ROLES.filter((r) => ["tenant", "landlord", "guarantor", "vendor"].includes(r.value)).map((r) => ({
      to: `/app/clients?role=${r.value}`,
      label: r.plural,
    })),
  ];
  const current = role ? `/app/clients?role=${role}` : "/app/clients";

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${rows.length} ${rows.length === 1 ? "record" : "records"} · one contact list for ${vocab.tenants}, ${vocab.landlords}, guarantors and vendors`}
        description={`One contact list for everybody you deal with. Somebody can be a ${vocab.tenant} and a ${vocab.landlord} at once, so open a record to see everything they rent, own, owe and have paid.`}
        actions={can("party.edit") ? <LinkButton to="/app/clients/new" need="party.edit">Add a client</LinkButton> : null}
      />

      <Tabs tabs={tabs} current={current} />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder="Search name, phone or email…" />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={q ? `Nobody matched “${q}”.` : "No clients yet."}
            hint={`Add ${vocab.tenants} and ${vocab.landlords} here. The same person can be both — tick more than one role.`}
            action={can("party.edit") ? <LinkButton to="/app/clients/new" need="party.edit">Add a client</LinkButton> : undefined}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>Roles</th>
                <th>Phone</th>
                <th>Email</th>
                <th className="num">{titleCase(vocab.leases)}</th>
                <th className="num">Balance</th>
                {/* Unlabelled: the icons say what they do, and a heading over two buttons is
                    a word doing no work. */}
                <th />
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to={`/app/clients/${row.id}`} className="link">{row.name}</Link>
                    <OptionMark name={row.statusName} tone={row.statusTone} />
                  </span>
                </td>
                <td>
                  <span className="flex flex-wrap gap-1">
                    {(row.roles ?? []).map((r) => (
                      <Badge key={r} tone={r === "tenant" ? "info" : r === "landlord" ? "success" : "muted"}>
                        {PARTY_ROLES.find((p) => p.value === r)?.label ?? r}
                      </Badge>
                    ))}
                  </span>
                </td>
                <td className="text-ink-500">{row.phone ? <a href={telHref(row.phone)} className="hover:underline">{row.phone}</a> : "—"}</td>
                <td className="truncate text-ink-500">{row.email ?? "—"}</td>
                <td className="num tabular">{row.activeLeases || "—"}</td>
                <td className="num tabular">
                  {Number(row.balance) > 0 ? (
                    <span className="font-medium text-danger-700">{fmt.money(Number(row.balance))}</span>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>

                <td className="num">
                  {/*
                    The row already opens the record, but "open it and then find the button" is
                    two steps for the thing people do most. These are the two.
                  */}
                  <span className="inline-flex items-center gap-1">
                    {can("party.edit") ? (
                      <Link
                        to={`/app/clients/${row.id}/edit`}
                        title="Edit"
                        aria-label={`Edit ${row.name}`}
                        className="inline-flex size-7 items-center justify-center rounded-[var(--radius-control)] text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                      >
                        <IconPencil size={15} />
                      </Link>
                    ) : null}
                    {can("payment.record") ? (
                      <Link
                        to={`/app/payments/new?partyId=${row.id}`}
                        title="Record a payment"
                        aria-label={`Record a payment for ${row.name}`}
                        className="inline-flex size-7 items-center justify-center rounded-[var(--radius-control)] text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                      >
                        <IconReceipt size={15} />
                      </Link>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"clients"} />
      </Card>
    </div>
  );
}
